import React, { useState, useEffect } from 'react';
import { apiCall } from '../utils/api';
import { toast } from './ui/sonner';
import {
  Wallet, X, RefreshCw, Clock, Landmark,
} from 'lucide-react';

import OverviewTab from './wallet/OverviewTab';
import FinancialSummaryCard from './wallet/FinancialSummaryCard';
import FinancialTimeline from './wallet/FinancialTimeline';
import BankAccountsList from './wallet/BankAccountsList';
import TransactionsList from './wallet/TransactionsList';
import AddBankAccountModal from './wallet/AddBankAccountModal';
import TransferFundsModal from './wallet/TransferFundsModal';
import SendMoneyModal from './wallet/SendMoneyModal';

const EMPTY_ACCOUNT = {
  bank_name: '',
  account_type: 'checking',
  routing_number: '',
  account_number: '',
  account_holder_name: '',
  card_type: '',
  card_last_four: '',
  is_primary: false,
};

export default function WalletManagement({ onClose }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [wallet, setWallet] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [transferAmount, setTransferAmount] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [transferring, setTransferring] = useState(false);
  const [showAccountNumbers, setShowAccountNumbers] = useState({});
  const [newAccount, setNewAccount] = useState(EMPTY_ACCOUNT);

  const loadWalletData = async () => {
    setLoading(true);
    try {
      const [walletRes, accountsRes, transactionsRes] = await Promise.all([
        apiCall('/api/wallet'),
        apiCall('/api/wallet/bank-accounts'),
        apiCall('/api/wallet/transactions'),
      ]);
      setWallet(walletRes.wallet || { balance: walletRes.balance || 0 });
      setBankAccounts(accountsRes.accounts || []);
      setTransactions(transactionsRes.transactions || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWalletData();
  }, []);

  const handleAddAccount = async (e) => {
    e.preventDefault();
    if (!newAccount.bank_name || !newAccount.account_number || !newAccount.routing_number) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      await apiCall('/api/wallet/bank-accounts', {
        method: 'POST',
        body: JSON.stringify(newAccount),
      });
      toast.success('Bank account added successfully');
      setShowAddAccount(false);
      setNewAccount(EMPTY_ACCOUNT);
      loadWalletData();
    } catch (error) {
      toast.error(error.message || 'Failed to add bank account');
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm('Are you sure you want to remove this bank account?')) return;
    try {
      await apiCall(`/api/wallet/bank-accounts/${accountId}`, { method: 'DELETE' });
      toast.success('Bank account removed');
      loadWalletData();
    } catch (error) {
      toast.error(error.message || 'Failed to remove account');
    }
  };

  const handleSetPrimary = async (accountId) => {
    try {
      await apiCall(`/api/wallet/bank-accounts/${accountId}/primary`, { method: 'PUT' });
      toast.success('Primary account updated');
      loadWalletData();
    } catch (error) {
      toast.error(error.message || 'Failed to update primary account');
    }
  };

  const handleTransfer = async () => {
    if (!selectedAccount || !transferAmount) {
      toast.error('Please select an account and enter an amount');
      return;
    }
    const amount = parseFloat(transferAmount);
    if (Number.isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (amount > (wallet?.balance || 0)) {
      toast.error('Insufficient wallet balance');
      return;
    }
    setTransferring(true);
    try {
      await apiCall('/api/wallet/transfer', {
        method: 'POST',
        body: JSON.stringify({ bank_account_id: selectedAccount, amount }),
      });
      toast.success(`$${amount.toFixed(2)} transfer initiated`);
      setShowTransfer(false);
      setTransferAmount('');
      setSelectedAccount(null);
      loadWalletData();
    } catch (error) {
      toast.error(error.message || 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  const toggleShowAccount = (accountId) => {
    setShowAccountNumbers((prev) => ({ ...prev, [accountId]: !prev[accountId] }));
  };

  const balance = wallet?.balance || 0;
  const tabs = [
    { id: 'overview', label: 'Overview', icon: <Wallet className="w-4 h-4" /> },
    { id: 'accounts', label: 'Bank Accounts', icon: <Landmark className="w-4 h-4" /> },
    { id: 'transactions', label: 'Transactions', icon: <Clock className="w-4 h-4" /> },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="wallet-management-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <Wallet className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Wallet & Banking</h2>
                <p className="text-white/80">Manage your funds and bank accounts</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2.5 hover:bg-white/20 rounded-xl transition"
              data-testid="wallet-management-close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="mt-6 p-4 bg-white/10 backdrop-blur-sm rounded-xl">
            <p className="text-white/70 text-sm">Available Balance</p>
            <p className="text-4xl font-bold mt-1" data-testid="wallet-available-balance">${balance.toFixed(2)}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex gap-1 p-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition ${
                  activeTab === tab.id
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid={`wallet-tab-${tab.id}`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-emerald-600 animate-spin" />
            </div>
          ) : (
            <>
              {activeTab === 'overview' && (
                <div className="space-y-4">
                  <FinancialSummaryCard />
                  <FinancialTimeline />
                  <OverviewTab
                    transactions={transactions}
                    onTransfer={() => setShowTransfer(true)}
                    onSend={() => setShowSend(true)}
                    onAddAccount={() => { setActiveTab('accounts'); setShowAddAccount(true); }}
                  />
                </div>
              )}

              {activeTab === 'accounts' && (
                <BankAccountsList
                  bankAccounts={bankAccounts}
                  showAccountNumbers={showAccountNumbers}
                  onToggleShowAccount={toggleShowAccount}
                  onSetPrimary={handleSetPrimary}
                  onDelete={handleDeleteAccount}
                  onAddAccount={() => setShowAddAccount(true)}
                />
              )}

              {activeTab === 'transactions' && (
                <TransactionsList transactions={transactions} />
              )}
            </>
          )}
        </div>

        {showAddAccount && (
          <AddBankAccountModal
            newAccount={newAccount}
            setNewAccount={setNewAccount}
            onClose={() => setShowAddAccount(false)}
            onSubmit={handleAddAccount}
          />
        )}

        {showSend && (
          <SendMoneyModal
            balance={balance}
            onClose={() => setShowSend(false)}
            onSuccess={loadWalletData}
          />
        )}

        {showTransfer && (
          <TransferFundsModal
            balance={balance}
            bankAccounts={bankAccounts}
            selectedAccount={selectedAccount}
            setSelectedAccount={setSelectedAccount}
            transferAmount={transferAmount}
            setTransferAmount={setTransferAmount}
            transferring={transferring}
            onTransfer={handleTransfer}
            onClose={() => setShowTransfer(false)}
            onGotoAddAccount={() => { setShowTransfer(false); setActiveTab('accounts'); setShowAddAccount(true); }}
          />
        )}
      </div>
    </div>
  );
}
